import { RPCClient } from '@buffer-mono/legacy-rpc-utils'

import {
  redirectToAuthorize,
  redirectToImpersonateStop,
  redirectToOidcLogout,
  refreshAccessToken,
} from '~publish/helpers/oidcAuth'

// RPCClient throws an Error with this exact message on a 401 response
// (see @buffer-mono/legacy-rpc-utils micro-rpc-client/RPCClient).
const UNAUTHORIZED_ERROR_MESSAGE = 'Unauthorized request'

const isUnauthorizedError = (error: unknown): boolean =>
  error instanceof Error && error.message === UNAUTHORIZED_ERROR_MESSAGE

// No onUnauthorized handler: callRpc (and createAuthedRpcCaller in the store
// middleware) own the 401 response with refresh-and-retry.
const rpc = new RPCClient({
  url: '/rpc',
  sendCredentials: 'same-origin',
})

/**
 * Calls a legacy RPC method.
 *
 * On a 401, silently refreshes the access token (shared single-flight +
 * cross-tab-locked helper) and replays the call once. The refresh endpoint is
 * the authority on session type: a missing (`no_session`) or rejected (`failed`)
 * refresh token re-authorizes through the authorize route. If the refresh
 * reports an impersonated session, stop impersonation so the advocate lands
 * back on their own account. If the replay is still unauthorized, redirects to
 * OIDC logout — a single retry only, never a loop.
 * A refresh that could not be carried out at all redirects
 * nowhere: the session is not known to be over, so the 401 propagates as an
 * ordinary error. Non-auth errors propagate unchanged.
 */
export default async function callRpc(
  methodName: string,
  args?: unknown,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- legacy RPC callers consume the result untyped
): Promise<any> {
  try {
    return await rpc.call(methodName, args)
  } catch (error) {
    if (!isUnauthorizedError(error)) {
      throw error
    }

    const result = await refreshAccessToken()

    if (result === 'unavailable') {
      throw error
    }

    if (result === 'impersonation') {
      redirectToImpersonateStop()

      throw error
    }

    if (result !== 'refreshed') {
      redirectToAuthorize()

      throw error
    }

    // Single retry only — never loop. A second auth rejection means the fresh
    // token is still not accepted, so log out.
    try {
      return await rpc.call(methodName, args)
    } catch (retryError) {
      if (isUnauthorizedError(retryError)) {
        redirectToOidcLogout()
      }

      throw retryError
    }
  }
}

export { rpc }
